<script setup lang="ts">
import { useQuery } from '@urql/vue';
import { graphql } from '@/gql';
import type { PartnerType } from '../types';
import PartnerWorkPlaceMap, {
  type MapPartnerLocation,
} from './PartnerWorkPlaceMap.vue';
import { useRoutes } from '@glow/shared/lib/routes';
import { MapIcon } from '@glow/shared/components/icons';
import type { AppSelectItem } from '@glow/shared/types/ui/AppSelect';

const props = defineProps<{
  partnerId: number;
  partnerType: PartnerType;
}>();
const emit = defineEmits<{
  (e: 'select', index: number): void;
}>();

const { data: masterQueryData } = useQuery({
  query: graphql(/* GraphQL */ `
    query PartnerWorkPlaceMap_MasterQuery($id: Int!) {
      master(id: $id) {
        id
        workPlaces {
          id
          address
          location
          metroStations {
            id
            name
            color
            location
          }
          attachedPhotos {
            id
            url
          }
          company {
            id
            slug
            name
            city {
              id
              slug
            }
          }
        }
      }
    }
  `),
  variables: computed(() => ({ id: props.partnerId })),
  pause: toRef(() => props.partnerType !== 'master'),
});

const { data: companyQueryData } = useQuery({
  query: graphql(/* GraphQL */ `
    query PartnerWorkPlaceMap_CompanyQuery($id: Int!) {
      company(id: $id) {
        id
        name
        location
        attachedPhotos {
          id
          url
        }
        metroStations {
          id
          name
          color
          location
        }
      }
    }
  `),
  variables: computed(() => ({ id: props.partnerId })),
  pause: toRef(() => props.partnerType !== 'company'),
});

const { getUrl } = useRoutes();
const locationIndex = ref(0);
const locations = computed<MapPartnerLocation[]>(() => {
  if (props.partnerType === 'master')
    return (
      masterQueryData.value?.master.workPlaces.map((workPlace) => ({
        id: workPlace.id,
        name: workPlace.company?.name ?? workPlace.address,
        location: workPlace.location,
        link: workPlace.company
          ? getUrl('partnerDetails', {
              type: 'company',
              city: workPlace.company.city.slug,
              partner: workPlace.company.slug,
            })
          : undefined,
        metroStations: workPlace.metroStations,
        photoUrls: workPlace.attachedPhotos.map((photo) => photo.url),
      })) ?? []
    );
  else if (companyQueryData.value) {
    const { company } = companyQueryData.value;
    return [
      {
        id: company.id,
        name: company.name,
        location: company.location,
        metroStations: company.metroStations,
        photoUrls: company.attachedPhotos.map((photo) => photo.url),
      },
    ];
  }
  return [];
});
const selectItems = computed(() =>
  locations.value.map<AppSelectItem<number>>((location, i) => ({
    value: i,
    title: location.name,
  }))
);
</script>

<template>
  <div class="page-card d-flex flex-column gap-4 pb-6 pt-0 pt-sm-6">
    <h2 class="d-none d-sm-block px-6">
      {{
        locations.length > 1
          ? 'Выберите рабочее место мастера'
          : 'Рабочее место'
      }}
    </h2>
    <div class="d-none d-md-flex mx-6">
      <AppSelect
        v-if="locations.length > 1"
        v-model="locationIndex"
        fluid
        static-placeholder
        placeholder="Выберите рабочее место"
        :items="selectItems"
      >
        <template #prepend>
          <AppIcon :icon="MapIcon" color="accent" />
        </template>
      </AppSelect>
    </div>
    <PartnerWorkPlaceMap
      v-model:location-index="locationIndex"
      class="px-sm-6"
      :locations="locations"
    />
    <div v-if="locations.length > 1" class="text-black-secondary mx-4 mx-sm-6">
      Будут показаны услуги и отзывы только с выбранного рабочего места.
      Обратите внимание, что имеются услуги, доступные исключительно в
      определенном рабочем месте.
    </div>
  </div>
</template>
